"use client";

import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { Activity } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";

const TONES = {
  violet: { stroke: "rgba(151,71,255,0.95)", fill: "rgba(151,71,255,0.18)" },
  pink: { stroke: "rgba(236,72,153,0.95)", fill: "rgba(236,72,153,0.16)" },
  amber: { stroke: "rgba(251,191,36,0.95)", fill: "rgba(251,191,36,0.16)" },
};

/**
 * Format a bucket start for the axis, at the granularity the server chose.
 *
 * The server picks `hour`, `day` or `week` from the span, and says which in
 * `period.bucket`. Labelling a daily bucket with a time (or an hourly one without)
 * is how a chart ends up quietly lying about what a point covers.
 */
function formatBucket(iso, bucket) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso ?? "");

  if (bucket === "hour") {
    return date.toLocaleTimeString(undefined, { hour: "numeric" });
  }
  // A week bucket is labelled by the day it starts — "week of Mar 3".
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * One time series from the dashboard payload, as a filled trend line.
 *
 * `data` is the backend's SeriesPoint[]: `{ bucket, value }`, with empty buckets
 * present at value 0 — so gaps render as real zeroes rather than the line hopping
 * over days that had no activity.
 *
 * Replaces AIMetricsChart and UsageAnalyticsChart, which hardcoded "last 6 months"
 * and "last 24 hours" in their titles while the window is now the user's to pick,
 * and mapped field names that no longer exist.
 */
export function SeriesChart({
  data = [],
  bucket = "day",
  title,
  description,
  valueLabel = "Value",
  tone = "violet",
  formatValue,
  className,
}) {
  const palette = TONES[tone] || TONES.violet;

  const chartData = (Array.isArray(data) ? data : []).map((point) => ({
    label: formatBucket(point?.bucket, bucket),
    value: Number(point?.value) || 0,
  }));

  // A single point is not a trend — an area chart of one bucket renders as a dot
  // and reads as "no data" anyway.
  const hasData = chartData.length >= 2 && chartData.some((p) => p.value > 0);

  return (
    <Card className={`overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_45px_rgba(36,20,82,0.45)] ${className || ""}`}>
      <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
        <CardTitle className="text-lg text-gray-100 sm:text-xl">{title}</CardTitle>
        {description && (
          <CardDescription className="text-xs text-gray-300 sm:text-sm">
            {description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-2 pt-0 sm:p-6 sm:pt-0">
        {hasData ? (
          <ChartContainer
            config={{
              value: { label: valueLabel, color: palette.stroke },
            }}
            className="h-[260px] w-full"
          >
            <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`series-${tone}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.fill} />
                  <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                minTickGap={24}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                width={44}
                tickFormatter={(v) => (formatValue ? formatValue(v) : v)}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="none"
                fill={`url(#series-${tone})`}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={palette.stroke}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ChartContainer>
        ) : (
          <EmptySeries />
        )}
      </CardContent>
    </Card>
  );
}

function EmptySeries() {
  return (
    <div className="flex h-[260px] flex-col items-center justify-center gap-2 text-center">
      <Activity className="h-8 w-8 text-gray-600" strokeWidth={1.5} />
      <p className="text-sm text-gray-400">No activity in this period</p>
      <p className="text-xs text-gray-500">Try a longer window, or a different project.</p>
    </div>
  );
}

export default SeriesChart;
